import { EmotionType } from '../../../types/FeedbackTypes'

export interface EmotionConfig {
  labelKey: string
  color: string
  imagePath: string
}

/**
 * Helper function to get image path for an emotion
 * Handles special case for embarrassed (filename has typo)
 */
const getEmotionImagePath = (emotion: EmotionType): string => {
  // Special case: embarrassed enum value maps to embarassed filename (typo in filename)
  if (emotion === EmotionType.EMBARRASSED) {
    return '/assets/img/emotions/emotion_embarassed.png'
  }
  return `/assets/img/emotions/emotion_${emotion}.png`
}

/**
 * Emotion configurations with PNG image paths
 */
export const EMOTION_CONFIGS: Record<EmotionType, Omit<EmotionConfig, 'labelKey'>> = {
  [EmotionType.HAPPY]: {
    color: '#2BE17E', // Green
    imagePath: getEmotionImagePath(EmotionType.HAPPY)
  },
  [EmotionType.SAD]: {
    color: '#48BAFF', // Blue
    imagePath: getEmotionImagePath(EmotionType.SAD)
  },
  [EmotionType.ANGRY]: {
    color: '#FF6666', // Red
    imagePath: getEmotionImagePath(EmotionType.ANGRY)
  },
  [EmotionType.STRESSED]: {
    color: '#FFAB40', // Orange
    imagePath: getEmotionImagePath(EmotionType.STRESSED)
  },
  [EmotionType.ANXIOUS]: {
    color: '#D166FF', // Purple
    imagePath: getEmotionImagePath(EmotionType.ANXIOUS)
  },
  [EmotionType.EXCITED]: {
    color: '#FFEF70', // Yellow
    imagePath: getEmotionImagePath(EmotionType.EXCITED)
  },
  [EmotionType.CALM]: {
    color: '#2BCAFF', // Cyan
    imagePath: getEmotionImagePath(EmotionType.CALM)
  },
  [EmotionType.BORED]: {
    color: '#9CA3AF', // Gray
    imagePath: getEmotionImagePath(EmotionType.BORED)
  },
  [EmotionType.CONTENT]: {
    color: '#60A5FA', // Light Blue
    imagePath: getEmotionImagePath(EmotionType.CONTENT)
  },
  [EmotionType.DISAPPOINTED]: {
    color: '#818CF8', // Indigo
    imagePath: getEmotionImagePath(EmotionType.DISAPPOINTED)
  },
  [EmotionType.EMBARRASSED]: {
    color: '#F472B6', // Pink
    imagePath: getEmotionImagePath(EmotionType.EMBARRASSED)
  },
  [EmotionType.IRRITATED]: {
    color: '#FB7185', // Rose
    imagePath: getEmotionImagePath(EmotionType.IRRITATED)
  },
  [EmotionType.UPSET]: {
    color: '#F87171', // Light Red
    imagePath: getEmotionImagePath(EmotionType.UPSET)
  },
  [EmotionType.WORRIED]: {
    color: '#A78BFA', // Violet
    imagePath: getEmotionImagePath(EmotionType.WORRIED)
  }
}

/**
 * Get emotion configuration by emotion type
 */
export const getEmotionConfig = (emotion: EmotionType | string): EmotionConfig => {
  const emotionKey = emotion.toLowerCase() as EmotionType
  const config = EMOTION_CONFIGS[emotionKey]

  if (!config) {
    // Default to calm if emotion not found
    return {
      labelKey: 'gamer.feedback.emotions.calm',
      ...EMOTION_CONFIGS[EmotionType.CALM]
    }
  }

  return {
    labelKey: `gamer.feedback.emotions.${emotionKey}`,
    ...config
  }
}
